// File: src/components/forms/TagInputField/TagInputField.tsx

import React, {
  useState,
  KeyboardEvent,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styled from "styled-components";
import { X } from "lucide-react";
import { TextField } from "../TextField";
import { BaseFieldProps } from "../types/fieldTypes";
import { motion, AnimatePresence } from "framer-motion";

export interface TagInputFieldProps extends BaseFieldProps {
  maxTags?: number;
  maxLength?: number;
  allowDuplicates?: boolean;
  onAdd?: (tag: string) => void;
  onRemove?: (removed: string, index: number) => void;
}

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surface};
  transition: border-color 0.2s ease;
  min-height: 48px;
  width: 100%;
  cursor: text;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TagItem = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.buttonText};
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: ${({ theme }) => theme.typography.caption.fontWeight};
  border-radius: ${({ theme }) => theme.radii.sm};
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: 6px;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryHover};
  }
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 100px;
`;

export const TagInputField = forwardRef<HTMLInputElement, TagInputFieldProps>(
  (
    {
      label,
      placeholder = "Add a tag...",
      maxTags = 10,
      maxLength = 20,
      allowDuplicates = false,
      value = "",
      onChange,
      onAdd,
      onRemove,
      ...props
    },
    ref
  ) => {
    const [tags, setTags] = useState<string[]>(
      value.split(",").map((t) => t.trim()).filter(Boolean)
    );
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => inputRef.current!);

    useEffect(() => {
      const incoming = value
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      setTags(incoming);
    }, [value]);

    const emitChange = (newTags: string[]) => {
      const joined = newTags.join(",");
      onChange?.({ target: { value: joined } } as React.ChangeEvent<HTMLInputElement>);
    };

    const addTag = (tag: string) => {
      const trimmed = tag.trim();
      if (!trimmed) return;
      if (!allowDuplicates && tags.includes(trimmed)) return;
      if (tags.length >= maxTags) return;

      const newTags = [...tags, trimmed];
      setTags(newTags);
      setInputValue("");
      emitChange(newTags);
      onAdd?.(trimmed);
    };

    const removeTag = (index: number) => {
      const removed = tags[index];
      const updated = tags.filter((_, i) => i !== index);
      setTags(updated);
      emitChange(updated);
      onRemove?.(removed, index);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (["Enter", ",", " "].includes(e.key)) {
        e.preventDefault();
        if (inputValue.trim()) addTag(inputValue);
      } else if (e.key === "Backspace" && !inputValue && tags.length) {
        removeTag(tags.length - 1);
      }
    };

    return (
      <div>
        {label && <label><strong>{label}</strong></label>}
        <TagContainer onClick={() => inputRef.current?.focus()}>
          <AnimatePresence>
            {tags.map((tag, idx) => (
              <TagItem
                key={tag + idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
              >
                {tag}
                <RemoveButton onClick={() => removeTag(idx)} aria-label="Remove tag">
                  <X size={14} />
                </RemoveButton>
              </TagItem>
            ))}
          </AnimatePresence>

          {tags.length < maxTags && (
            <InputWrapper>
              <TextField
                {...props}
                id="tag-input"
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={(e) =>
                  e.target.value.length <= maxLength && setInputValue(e.target.value)
                }
                onKeyDown={handleKeyDown}
              />
            </InputWrapper>
          )}
        </TagContainer>
      </div>
    );
  }
);

TagInputField.displayName = "TagInputField";